import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, TrendingUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import DetailedCostsSection from './DetailedCostsSection';
import AdditionalMetricsSection from './AdditionalMetricsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета рентабельности
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  historicalData,
  setHistoricalData,
  industryTypes,
  companySizes,
  analysisPeriods,
  saveAnalysis 
}) => {
  
  // Состояние для имени сохраняемого анализа
  const [saveAnalysisName, setSaveAnalysisName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeDetailedCosts' && !checked) {
        // Вычислим общую сумму детализированных расходов
        const totalDetailedCosts = parseFloatOrDefault(prev.materialCosts, 0) +
                                  parseFloatOrDefault(prev.laborCosts, 0) +
                                  parseFloatOrDefault(prev.rentCosts, 0) +
                                  parseFloatOrDefault(prev.utilitiesCosts, 0) +
                                  parseFloatOrDefault(prev.marketingCosts, 0) +
                                  parseFloatOrDefault(prev.logisticsCosts, 0) +
                                  parseFloatOrDefault(prev.adminCosts, 0) +
                                  parseFloatOrDefault(prev.otherCosts, 0);
        
        // Обновляем основные параметры, сохраняя общую сумму расходов
        const costOfGoods = Math.round(totalDetailedCosts * 0.7); // 70% от общих расходов
        const operatingExpenses = Math.round(totalDetailedCosts * 0.3); // 30% от общих расходов
        
        newState.costOfGoods = costOfGoods.toString();
        newState.operatingExpenses = operatingExpenses.toString();
      }
      
      if (name === 'includeAdditionalMetrics' && !checked) {
        newState.interestExpense = '0';
        newState.depreciation = '0';
        newState.accountsReceivable = '0';
        newState.accountsPayable = '0';
        newState.inventory = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      revenue, costOfGoods, operatingExpenses, taxRate, investmentAmount,
      includeDetailedCosts, materialCosts, laborCosts, rentCosts, utilitiesCosts,
      marketingCosts, logisticsCosts, adminCosts, otherCosts,
      includeAdditionalMetrics, interestExpense, depreciation, accountsReceivable,
      accountsPayable, inventory
    } = formData;

    // Валидация основных параметров
    const numRevenue = parseFloatOrDefault(revenue, -1);
    const numCostOfGoods = parseFloatOrDefault(costOfGoods, -1);
    const numOperatingExpenses = parseFloatOrDefault(operatingExpenses, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const numInvestmentAmount = parseFloatOrDefault(investmentAmount, -1);

    if (numRevenue <= 0) newErrors.revenue = 'Выручка должна быть положительной.';
    if (!newErrors.revenue && numRevenue > 1000000000000) newErrors.revenue = 'Значение слишком большое.';
    
    if (numCostOfGoods < 0) newErrors.costOfGoods = 'Себестоимость не может быть отрицательной.';
    if (!newErrors.costOfGoods && numCostOfGoods > numRevenue) newErrors.costOfGoods = 'Себестоимость не может превышать выручку.';
    
    if (numOperatingExpenses < 0) newErrors.operatingExpenses = 'Операционные расходы не могут быть отрицательными.';
    if (!newErrors.operatingExpenses && (numOperatingExpenses + numCostOfGoods) > numRevenue * 3) {
      newErrors.operatingExpenses = 'Слишком большие расходы относительно выручки.';
    }
    
    if (numTaxRate < 0) newErrors.taxRate = 'Ставка налога не может быть отрицательной.';
    if (!newErrors.taxRate && numTaxRate > 100) newErrors.taxRate = 'Ставка налога не может быть больше 100%.';
    
    if (numInvestmentAmount <= 0) newErrors.investmentAmount = 'Сумма инвестиций должна быть положительной.';

    // Валидация детализированных расходов
    if (includeDetailedCosts) {
      const numMaterialCosts = parseFloatOrDefault(materialCosts, -1);
      const numLaborCosts = parseFloatOrDefault(laborCosts, -1);
      const numRentCosts = parseFloatOrDefault(rentCosts, -1);
      const numUtilitiesCosts = parseFloatOrDefault(utilitiesCosts, -1);
      const numMarketingCosts = parseFloatOrDefault(marketingCosts, -1);
      const numLogisticsCosts = parseFloatOrDefault(logisticsCosts, -1);
      const numAdminCosts = parseFloatOrDefault(adminCosts, -1);
      const numOtherCosts = parseFloatOrDefault(otherCosts, -1);
      
      if (numMaterialCosts < 0) newErrors.materialCosts = 'Значение не может быть отрицательным.';
      if (numLaborCosts < 0) newErrors.laborCosts = 'Значение не может быть отрицательным.';
      if (numRentCosts < 0) newErrors.rentCosts = 'Значение не может быть отрицательным.';
      if (numUtilitiesCosts < 0) newErrors.utilitiesCosts = 'Значение не может быть отрицательным.';
      if (numMarketingCosts < 0) newErrors.marketingCosts = 'Значение не может быть отрицательным.';
      if (numLogisticsCosts < 0) newErrors.logisticsCosts = 'Значение не может быть отрицательным.';
      if (numAdminCosts < 0) newErrors.adminCosts = 'Значение не может быть отрицательным.';
      if (numOtherCosts < 0) newErrors.otherCosts = 'Значение не может быть отрицательным.';
      
      // Проверка суммы детализированных расходов
      const totalDetailedCosts = numMaterialCosts + numLaborCosts + numRentCosts + 
                               numUtilitiesCosts + numMarketingCosts + numLogisticsCosts + 
                               numAdminCosts + numOtherCosts;
      
      const totalRegularCosts = numCostOfGoods + numOperatingExpenses;
      
      if (Math.abs(totalDetailedCosts - totalRegularCosts) > 1) {
        newErrors.detailedCostsTotal = `Сумма детализированных расходов (${totalDetailedCosts.toLocaleString('ru-RU')} ₽) не соответствует сумме себестоимости и операционных расходов (${totalRegularCosts.toLocaleString('ru-RU')} ₽).`;
      }
    }

    // Валидация дополнительных финансовых показателей
    if (includeAdditionalMetrics) {
      const numInterestExpense = parseFloatOrDefault(interestExpense, -1);
      const numDepreciation = parseFloatOrDefault(depreciation, -1);
      const numAccountsReceivable = parseFloatOrDefault(accountsReceivable, -1);
      const numAccountsPayable = parseFloatOrDefault(accountsPayable, -1);
      const numInventory = parseFloatOrDefault(inventory, -1);
      
      if (numInterestExpense < 0) newErrors.interestExpense = 'Значение не может быть отрицательным.';
      if (numDepreciation < 0) newErrors.depreciation = 'Значение не может быть отрицательным.';
      if (numAccountsReceivable < 0) newErrors.accountsReceivable = 'Значение не может быть отрицательным.';
      if (numAccountsPayable < 0) newErrors.accountsPayable = 'Значение не может быть отрицательным.';
      if (numInventory < 0) newErrors.inventory = 'Значение не может быть отрицательным.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета показателей рентабельности
  const calculateProfitability = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numRevenue = parseFloatOrDefault(formData.revenue);
        const numCostOfGoods = parseFloatOrDefault(formData.costOfGoods);
        const numOperatingExpenses = parseFloatOrDefault(formData.operatingExpenses);
        const numTaxRate = parseFloatOrDefault(formData.taxRate) / 100; // Переводим в коэффициент
        const numInvestmentAmount = parseFloatOrDefault(formData.investmentAmount);
        
        // Дополнительные показатели (если включены)
        const numInterestExpense = formData.includeAdditionalMetrics 
          ? parseFloatOrDefault(formData.interestExpense) : 0;
        const numDepreciation = formData.includeAdditionalMetrics 
          ? parseFloatOrDefault(formData.depreciation) : 0;
        const numAccountsReceivable = formData.includeAdditionalMetrics 
          ? parseFloatOrDefault(formData.accountsReceivable) : 0;
        const numAccountsPayable = formData.includeAdditionalMetrics 
          ? parseFloatOrDefault(formData.accountsPayable) : 0;
        const numInventory = formData.includeAdditionalMetrics 
          ? parseFloatOrDefault(formData.inventory) : 0;

        // --- Расчеты основных показателей ---
        
        // Валовая прибыль (Gross Profit) = Выручка - Себестоимость
        const grossProfit = numRevenue - numCostOfGoods;
        
        // Операционная прибыль (EBIT) = Валовая прибыль - Операционные расходы
        const operatingProfit = grossProfit - numOperatingExpenses;
        
        // Прибыль до налогообложения = Операционная прибыль - Процентные расходы
        const earningsBeforeTax = operatingProfit - numInterestExpense;
        
        // Чистая прибыль = Прибыль до налогообложения * (1 - Ставка налога)
        const netProfit = earningsBeforeTax * (1 - numTaxRate);
        
        // --- Расчет показателей рентабельности ---
        
        // Валовая маржа (Gross Profit Margin) = Валовая прибыль / Выручка * 100%
        const grossProfitMargin = (grossProfit / numRevenue) * 100;
        
        // Операционная маржа (Operating Margin) = Операционная прибыль / Выручка * 100%
        const operatingMargin = (operatingProfit / numRevenue) * 100;
        
        // Чистая маржа (Net Profit Margin) = Чистая прибыль / Выручка * 100%
        const netProfitMargin = (netProfit / numRevenue) * 100;
        
        // Рентабельность инвестиций (ROI) = Чистая прибыль / Инвестиции * 100%
        const roi = (netProfit / numInvestmentAmount) * 100;
        
        // Рентабельность активов (ROA) = Чистая прибыль / (Инвестиции + Дебиторка + Запасы) * 100%
        const totalAssets = numInvestmentAmount + numAccountsReceivable + numInventory;
        const roa = (netProfit / totalAssets) * 100;
        
        // Рентабельность собственного капитала (ROE) = Чистая прибыль / (Инвестиции - Кредиторка) * 100%
        const equity = numInvestmentAmount - numAccountsPayable;
        const roe = (netProfit / Math.max(equity, 1)) * 100; // Избегаем деления на ноль
        
        // EBITDA = Операционная прибыль + Амортизация
        const ebitda = operatingProfit + numDepreciation;
        
        // Рентабельность по EBITDA = EBITDA / Выручка * 100%
        const ebitdaMargin = (ebitda / numRevenue) * 100;
        
        // Отношение долга к EBITDA = Кредиторская задолженность / EBITDA
        const debtToEbitda = ebitda !== 0 ? numAccountsPayable / ebitda : 0;
        
        // Операционный рычаг (Operating Leverage) = Валовая прибыль / Операционная прибыль
        const operatingLeverage = operatingProfit !== 0 ? grossProfit / operatingProfit : 0;
        
        // Запас финансовой прочности = (Выручка - Точка безубыточности) / Выручка * 100%
        const breakEvenPoint = numOperatingExpenses / (grossProfit / numRevenue);
        const financialSafety = ((numRevenue - breakEvenPoint) / numRevenue) * 100;

        // --- Формирование отчета о результатах ---
        const financialResults = {
          // Абсолютные показатели
          revenue: numRevenue,
          costOfGoods: numCostOfGoods,
          operatingExpenses: numOperatingExpenses,
          grossProfit: grossProfit,
          operatingProfit: operatingProfit,
          netProfit: netProfit,
          ebitda: ebitda,
          
          // Относительные показатели (в %)
          grossProfitMargin: grossProfitMargin,
          operatingMargin: operatingMargin,
          netProfitMargin: netProfitMargin,
          roi: roi,
          roa: roa,
          roe: roe,
          ebitdaMargin: ebitdaMargin,
          
          // Дополнительные показатели
          debtToEbitda: debtToEbitda,
          operatingLeverage: operatingLeverage,
          breakEvenPoint: breakEvenPoint,
          financialSafety: financialSafety,
          
          // Дополнительные данные
          taxRate: numTaxRate,
          investmentAmount: numInvestmentAmount,
          interestExpense: numInterestExpense,
          depreciation: numDepreciation,
          accountsReceivable: numAccountsReceivable,
          accountsPayable: numAccountsPayable,
          inventory: numInventory,
          
          // Метаданные для анализа
          industryType: formData.industryType,
          companySize: formData.companySize,
          period: formData.period,
          calculationDate: new Date().toISOString()
        };

        // --- Обновление исторических данных ---
        const newHistoricalData = [...historicalData];
        
        // Ищем, есть ли уже данные за этот период
        const existingPeriodIndex = newHistoricalData.findIndex(
          entry => entry.period === formData.period
        );
        
        if (existingPeriodIndex !== -1) {
          // Обновляем существующий период
          newHistoricalData[existingPeriodIndex] = {
            ...financialResults,
            period: formData.period,
            date: new Date().toISOString()
          };
        } else {
          // Добавляем новый период
          newHistoricalData.push({
            ...financialResults,
            period: formData.period,
            date: new Date().toISOString()
          });
        }
        
        // Сортируем исторические данные
        newHistoricalData.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Обновляем состояние с результатами и историческими данными
        setResults(financialResults);
        setHistoricalData(newHistoricalData);

      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, historicalData, setHistoricalData, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      revenue: '1000000',
      costOfGoods: '600000',
      operatingExpenses: '200000',
      taxRate: '20',
      investmentAmount: '2000000',
      industryType: 'retail',
      companySize: 'small',
      period: 'year',
      includeDetailedCosts: false,
      materialCosts: '300000',
      laborCosts: '150000',
      rentCosts: '50000',
      utilitiesCosts: '20000',
      marketingCosts: '40000',
      logisticsCosts: '30000',
      adminCosts: '60000',
      otherCosts: '10000',
      includeAdditionalMetrics: false,
      interestExpense: '10000',
      depreciation: '50000',
      accountsReceivable: '200000',
      accountsPayable: '150000',
      inventory: '300000',
    });
    setResults(null);
    setErrors({});
    setSaveAnalysisName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = [
      'Показатель', 'Значение', 'Единица измерения'
    ];

    const formatNum = (num) => num.toFixed(2).replace('.', ',');

    const rows = [
      ['Отчетный период', formData.period === 'year' ? 'Год' : formData.period === 'quarter' ? 'Квартал' : formData.period === 'half_year' ? 'Полугодие' : 'Месяц', ''],
      ['Отрасль', industryTypes.find(i => i.id === formData.industryType)?.name || formData.industryType, ''],
      ['', '', ''],
      ['ИСХОДНЫЕ ДАННЫЕ', '', ''],
      ['Выручка', formatNum(results.revenue), 'руб.'],
      ['Себестоимость', formatNum(results.costOfGoods), 'руб.'],
      ['Операционные расходы', formatNum(results.operatingExpenses), 'руб.'],
      ['Сумма инвестиций/активов', formatNum(results.investmentAmount), 'руб.'],
      ['Ставка налога на прибыль', formatNum(results.taxRate * 100), '%'],
      ['', '', ''],
      ['ФИНАНСОВЫЕ РЕЗУЛЬТАТЫ', '', ''],
      ['Валовая прибыль', formatNum(results.grossProfit), 'руб.'],
      ['Операционная прибыль (EBIT)', formatNum(results.operatingProfit), 'руб.'],
      ['EBITDA', formatNum(results.ebitda), 'руб.'],
      ['Чистая прибыль', formatNum(results.netProfit), 'руб.'],
      ['', '', ''],
      ['ПОКАЗАТЕЛИ РЕНТАБЕЛЬНОСТИ', '', ''],
      ['Валовая маржа', formatNum(results.grossProfitMargin), '%'],
      ['Операционная маржа', formatNum(results.operatingMargin), '%'],
      ['Чистая маржа', formatNum(results.netProfitMargin), '%'],
      ['Рентабельность инвестиций (ROI)', formatNum(results.roi), '%'],
      ['Рентабельность активов (ROA)', formatNum(results.roa), '%'],
      ['Рентабельность собственного капитала (ROE)', formatNum(results.roe), '%'],
      ['Рентабельность по EBITDA', formatNum(results.ebitdaMargin), '%'],
      ['', '', ''],
      ['ДОПОЛНИТЕЛЬНЫЕ ПОКАЗАТЕЛИ', '', ''],
      ['Точка безубыточности', formatNum(results.breakEvenPoint), 'руб.'],
      ['Запас финансовой прочности', formatNum(results.financialSafety), '%'],
      ['Операционный рычаг', formatNum(results.operatingLeverage), 'x'],
      ['Отношение долга к EBITDA', formatNum(results.debtToEbitda), 'x']
    ];

    // Добавляем дополнительные метрики, если они включены
    if (formData.includeAdditionalMetrics) {
      rows.push(['', '', '']);
      rows.push(['РАСШИРЕННЫЕ ФИНАНСОВЫЕ ПОКАЗАТЕЛИ', '', '']);
      rows.push(['Процентные расходы', formatNum(results.interestExpense), 'руб.']);
      rows.push(['Амортизация', formatNum(results.depreciation), 'руб.']);
      rows.push(['Дебиторская задолженность', formatNum(results.accountsReceivable), 'руб.']);
      rows.push(['Кредиторская задолженность', formatNum(results.accountsPayable), 'руб.']);
      rows.push(['Запасы', formatNum(results.inventory), 'руб.']);
    }

    // Добавляем информацию о средних показателях по отрасли
    const industry = industryTypes.find(i => i.id === formData.industryType);
    if (industry) {
      rows.push(['', '', '']);
      rows.push(['СРЕДНИЕ ПОКАЗАТЕЛИ ПО ОТРАСЛИ', '', '']);
      rows.push(['Средняя валовая маржа в отрасли', formatNum(industry.avgGrossMargin), '%']);
      rows.push(['Средняя чистая маржа в отрасли', formatNum(industry.avgNetMargin), '%']);
      rows.push(['Средний ROI в отрасли', formatNum(industry.avgROI), '%']);
    }

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'анализ_рентабельности.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения анализа
  const handleSaveAnalysis = () => {
    if (showSaveDialog) {
      const savedResult = saveAnalysis(saveAnalysisName || null);
      if (savedResult) {
        alert(`Анализ "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveAnalysisName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateProfitability();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-emerald-500" />
          Расчет показателей рентабельности
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры бизнеса */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          industryTypes={industryTypes}
          companySizes={companySizes}
          analysisPeriods={analysisPeriods}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Детализация расходов */}
        <DetailedCostsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные финансовые показатели */}
        <AdditionalMetricsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения анализа */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveAnalysisName}
                onChange={(e) => setSaveAnalysisName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveAnalysis}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать рентабельность'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать отчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveAnalysis}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить анализ
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;